import type { MapDocument, WorldDocument } from "../document/types.ts";
import type { AreaOffset, AreaReach, AreaRect } from "./area.ts";
import { areaContents, type AreaContents, type PropExtentOf } from "./area-contents.ts";
import { areaReplacedBoxes, type AreaTileBox } from "./area-replaced.ts";
import { planAreaMove, type AreaMovePlan } from "./area-move.ts";
import type { AreaPastePlan } from "./area-paste.ts";

/**
 * What the area's view state has the planner work out, each kept until what it was worked out from changes
 * (ADR-128): what a rectangle holds, counted once for a map, a rectangle and a reach; the plan of a pending move,
 * planned once for a document, a rectangle, an offset and the layers taken; and the boxes of the paint that plan
 * replaces, found once for a plan. A document is replaced, never changed in place, so its identity is the whole of the
 * invalidation: every key below is compared on each read and nothing has to be told that something changed.
 */

const NO_BOXES: readonly AreaTileBox[] = [];

/**
 * Whether two rectangles are the same cells.
 *
 * @param a One rectangle, or null.
 * @param b The other, or null.
 * @returns True when equal.
 */
export function sameAreaRect(a: AreaRect | null, b: AreaRect | null): boolean {
  return a === b || (a !== null && b !== null && a.x0 === b.x0 && a.y0 === b.y0 && a.x1 === b.x1 && a.y1 === b.y1);
}

/**
 * The area's counts and plans, each remembered with what it was made from.
 */
export class AreaPlans {
  private extentOf: PropExtentOf | undefined = undefined;
  private counted: { readonly map: MapDocument; readonly rect: AreaRect; readonly key: string; readonly contents: AreaContents } | null = null;
  private planned: { readonly world: WorldDocument; readonly rect: AreaRect; readonly offset: AreaOffset; readonly key: string; readonly plan: AreaMovePlan } | null = null;
  private placed: { readonly plan: AreaMovePlan | AreaPastePlan; readonly boxes: readonly AreaTileBox[] } | null = null;

  /**
   * Binds each prop's extent on the map, which says whether a rectangle's edge cuts it (decision 11), and forgets
   * the counts made without it.
   *
   * @param extentOf The extent lookup.
   */
  public bindExtent(extentOf: PropExtentOf): void {
    this.extentOf = extentOf;
    this.counted = null;
  }

  /**
   * What a rectangle holds (`areaContents`).
   *
   * @param map The map in view.
   * @param rect The rectangle of tiles.
   * @param reach The layers taken and left.
   * @returns The contents.
   */
  public contents(map: MapDocument, rect: AreaRect, reach: AreaReach): AreaContents {
    const key = JSON.stringify(reach);
    const held = this.counted;
    if (held !== null && held.map === map && held.key === key && sameAreaRect(held.rect, rect)) {
      return held.contents;
    }
    const contents = areaContents(map, rect, reach, this.extentOf);
    this.counted = { map, rect, key, contents };
    return contents;
  }

  /**
   * The plan of a move (`planAreaMove`).
   *
   * @param world The world as it stands.
   * @param mapId The map in view.
   * @param rect The source rectangle.
   * @param offset The offset in whole tiles.
   * @param taken The layers the reach takes.
   * @returns The plan.
   */
  public movePlan(world: WorldDocument, mapId: string, rect: AreaRect, offset: AreaOffset, taken: readonly string[]): AreaMovePlan {
    const key = JSON.stringify(taken);
    const held = this.planned;
    if (held !== null && held.world === world && held.key === key && sameAreaRect(held.rect, rect) && held.offset.dx === offset.dx && held.offset.dy === offset.dy) {
      return held.plan;
    }
    const plan = planAreaMove(world, mapId, rect, offset, taken);
    this.planned = { world, rect, offset, key, plan };
    return plan;
  }

  /**
   * The boxes in tiles of the paint a plan replaces; none unless the plan is a move or a paste over paint.
   *
   * @param map The map the plan was made against.
   * @param plan The plan of a move or of a paste.
   * @returns The boxes.
   */
  public replacedBoxes(map: MapDocument, plan: AreaMovePlan | AreaPastePlan): readonly AreaTileBox[] {
    if ((plan.kind !== "move" && plan.kind !== "paste") || plan.replaced.length === 0) {
      return NO_BOXES;
    }
    if (this.placed === null || this.placed.plan !== plan) {
      this.placed = { plan, boxes: areaReplacedBoxes(map, plan.replaced) };
    }
    return this.placed.boxes;
  }
}
